/**
 * reCAPTCHA v3 hook.
 * Loads the script when site key is set and provides getToken(action).
 * Excluded from login page per requirement; use on register, forgot password, reset password.
 */

import { useState, useEffect, useCallback } from 'react';

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';
const SCRIPT_URL = 'https://www.google.com/recaptcha/api.js?render=' + SITE_KEY;

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

export function useRecaptcha(): {
  getToken: (action: string) => Promise<string>;
  isReady: boolean;
  error: string | null;
} {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!SITE_KEY) {
      setIsReady(true); // No key: skip recaptcha, backend will skip if no secret
      return;
    }

    if (window.grecaptcha) {
      setIsReady(true);
      return;
    }

    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => setIsReady(true));
      } else {
        setError('reCAPTCHA failed to load');
      }
    };
    script.onerror = () => setError('Failed to load reCAPTCHA script');
    document.head.appendChild(script);

    return () => {
      // Optional: remove script on unmount if you want; usually keep for other forms
    };
  }, []);

  const getToken = useCallback(
    async (action: string): Promise<string> => {
      if (!SITE_KEY) return '';
      if (error || !window.grecaptcha) return '';
      try {
        return await window.grecaptcha.execute(SITE_KEY, { action });
      } catch (e) {
        console.warn('Recaptcha execute failed', e);
        return '';
      }
    },
    [error]
  );

  return { getToken, isReady, error };
}
